import { PAGE_SIZES } from '../../../constants'
import { translate } from '../../../helpers/translations/translator'
import { TrackType } from '../../../models'
import { setPagination } from '../../../redux/pagination/paginationActions'
import { trackRemoved } from '../../../redux/tracks/tracksActions'
import { postCommentApiCall } from '../../entities/Comment/CommentService'
import { isFetchCollectionResultSuccessful, isFetchResultSuccessful } from '../../entities/FetchResult'
import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
} from '../../entities/NotificationCenter/NotificationCenter'
import {
    endEventLoggerApiCall,
    startEventLoggerApiCall,
} from '../../entities/NotificationCenter/NotificationCenterService'
import { Period } from '../../entities/Period/Period'
import { ExportTracksFormData, FinishedTracksSorting, TrackFormData } from '../../entities/Track/Track'
import {
    deleteTrackApiCall,
    getExportTracksApiCall,
    getTracksPaginationV3ApiCall,
    getTracksV3ApiCall,
    patchTrackApiCall,
    postTrackApiCall,
} from '../../entities/Track/TrackService'
import { generateDownloadURI } from '../../helpers/browser'
import trans from '../../helpers/trans'
import { getCheckedFormOptionsValue } from '../../helpers/types/formOption'
import { ToastType } from '../../types'
import { DepartmentOption } from '../../types/selectOptionTypes'
import { TypedDispatch } from '../store'
import { setToast } from '../toasts/toastsReducer'
import {
    setError as setUserError,
    setIsLoading as setIsUserLoading,
    setIsSuccessful as setIsUserSuccessful,
} from '../user/userReducer'
import {
    removeLoadingTrack,
    setError, setFinishedTracks,
    setIsLoading,
    setLoadingTrack,
    setTracksToReview,
    trackAdded,
    trackDeleted,
    trackPatched,
} from './tracksReducer'

export const exportTracks = (formData: ExportTracksFormData) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsUserLoading(true))
    dispatch(setIsUserSuccessful(false))
    dispatch(setUserError(''))

    try {
        const response = await getExportTracksApiCall(formData)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setUserError(response.error))

            return
        }

        generateDownloadURI(response.data.uri, response.data.fileName)

        dispatch(setIsUserSuccessful(true))
    } catch (error) {
        console.error('[exportTracks]', error)
        dispatch(setUserError(trans('errors.unknownError')))
    } finally {
        dispatch(setIsUserLoading(false))
    }
}

let getTracksAbortController: AbortController | null = null

export const getTracks = (
    trackType: TrackType,
    period: Period,
    page?: number,
    departmentOptions?: DepartmentOption[],
    employeeSearch?: string,
    employmentTypes?: string[],
    sorting?: FinishedTracksSorting,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    if (getTracksAbortController) {
        getTracksAbortController.abort()
    }

    getTracksAbortController = new AbortController()
    const { signal } = getTracksAbortController

    try {
        const filters = {
            ...(departmentOptions && { departments: getCheckedFormOptionsValue(departmentOptions) }),
            employeeSearch,
            trackType,
            employmentTypes,
        }
        const pageNumber = page || 1
        const pageSize = PAGE_SIZES.TRACKS[trackType]
        const pagination = {
            start: (pageNumber - 1) * pageSize,
            items: pageSize,
        }
        const response = await getTracksV3ApiCall(period, filters, pagination, signal, sorting)

        if (! isFetchCollectionResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        if (page === undefined) {
            const paginationResponse = await getTracksPaginationV3ApiCall(period, filters, {
                start: 0,
                items: pageSize,
            }, signal, sorting)

            if (! isFetchResultSuccessful(paginationResponse)) {
                dispatch(setError(paginationResponse.error))
            } else {
                dispatch(setPagination(paginationResponse.data.pages))
            }
        }

        if (trackType === TrackType.review) {
            dispatch(setTracksToReview(response.data))
        } else if (trackType === TrackType.finished) {
            dispatch(setFinishedTracks(response.data))
        }
    } catch (error) {
        if ((error as Error).name === 'AbortError') {
            return
        }

        console.error('[getTracks]', error)
    } finally {
        if (! signal.aborted) {
            dispatch(setIsLoading(false))
        }
    }
}

export const addTrack = (
    userId: string,
    departmentId: string,
    data: TrackFormData,
    comment: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.TRACKS,
        NotificationCenterAction.CREATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const response = await postTrackApiCall(data, userId, departmentId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        if (comment) {
            await postCommentApiCall({
                comment,
                relationshipKey: 'track',
                relationshipType: 'tracks',
                relationshipId: response.data.id,
                eventLogTrackerData: responseTracker.data,
            })
        }

        dispatch(trackAdded(response.data))

        dispatch(setToast({
            text: translate('pages.tracks.addTrackSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[addTrack]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsLoading(false))
    }
}

export const editTrack = (
    trackId: string,
    departmentId: string,
    data: Partial<TrackFormData>,
    comment?: string,
    approved?: boolean,
    endedOpenTrackFromManager?: boolean,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setLoadingTrack(trackId))

    const eventActionCategories = endedOpenTrackFromManager
        ? [NotificationCenterActionCategory.TRACK_IN_PROGRESS_ENDED_BY_MANAGER]
        : [NotificationCenterActionCategory.UNKNOWN]

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.TRACKS,
        NotificationCenterAction.UPDATED,
        eventActionCategories,
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        dispatch(removeLoadingTrack(trackId))
        return
    }

    try {
        const response = await patchTrackApiCall(data, trackId, departmentId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        if (comment) {
            await postCommentApiCall({
                comment,
                relationshipKey: 'track',
                relationshipType: 'tracks',
                relationshipId: response.data.id,
                eventLogTrackerData: responseTracker.data,
            })
        }

        dispatch(trackPatched(response.data))

        const toast = approved ? 'editAndApproveTrackSuccess' : 'editTrackSuccess'
        dispatch(setToast({
            text: translate(`pages.tracks.${toast}`),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[editTrack]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(removeLoadingTrack(trackId))
    }
}

export const approveTrack = (trackId: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(trackPatched({ id: trackId, accepted: true }))
    dispatch(setLoadingTrack(trackId))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.TRACKS,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        dispatch(removeLoadingTrack(trackId))
        dispatch(trackPatched({ id: trackId, accepted: false }))
        return
    }

    try {
        const response = await patchTrackApiCall({ accepted: true }, trackId, undefined, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(trackPatched(response.data))

        dispatch(setToast({
            text: translate('pages.tracks.approveTrackSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[approveTrack]', error)
        dispatch(trackPatched({ id: trackId, accepted: false }))
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(removeLoadingTrack(trackId))
    }
}

export const deleteTrack = (trackId: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setLoadingTrack(trackId))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.TRACKS,
        NotificationCenterAction.DELETED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        dispatch(removeLoadingTrack(trackId))
        return
    }

    try {
        const response = await deleteTrackApiCall(trackId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
        }

        dispatch(setToast({
            text: translate('pages.tracks.deleteTrackSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))

        dispatch(trackDeleted({ id: trackId }))

        dispatch(trackRemoved())
    } catch (error) {
        console.error('[deleteTrack]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(removeLoadingTrack(trackId))
    }
}
